import React, { useState } from "react";
import { DownloadableFilesT } from "get-life-storybook-ts/lib/components/DownloadableFiles/DownloadableFiles";

interface V3CoverageScopeProps {
  translate: (key: string) => string;
  downloadableFiles?: DownloadableFilesT;
  downloadDocument?: (type: string) => void;
}

type TabKey = "si" | "no";

const IconCheck = ({ size = 16 }: { size?: number }) => (
  <svg
    aria-hidden="true"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.6}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 12l4 4 10-10" />
  </svg>
);

const IconX = ({ size = 14 }: { size?: number }) => (
  <svg
    aria-hidden="true"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.6}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

const IconDoc = ({ size = 13 }: { size?: number }) => (
  <svg
    aria-hidden="true"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M7 3h7l5 5v13a1 1 0 01-1 1H7a1 1 0 01-1-1V4a1 1 0 011-1z" />
    <path d="M14 3v5h5" />
  </svg>
);

const YES_KEYS = ["1", "2", "3", "4", "5"] as const;
const NO_KEYS = ["1", "2", "3"] as const;

const V3CoverageScope: React.FC<V3CoverageScopeProps> = ({
  translate,
  downloadableFiles,
  downloadDocument,
}) => {
  const [tab, setTab] = useState<TabKey>("si");

  const yesItems = YES_KEYS.map((k) => ({
    title: translate(`newUpsell.v3.coverageScope.yes.${k}.title`),
    desc: translate(`newUpsell.v3.coverageScope.yes.${k}.desc`),
  }));

  const noItems = NO_KEYS.map((k) => ({
    title: translate(`newUpsell.v3.coverageScope.no.${k}.title`),
    desc: translate(`newUpsell.v3.coverageScope.no.${k}.desc`),
  }));

  const items = tab === "si" ? yesItems : noItems;

  const generalConditions = downloadableFiles?.find(
    (f) => f?.type === "offer-w-base" || f?.type === "offer-multi"
  );
  const legalNote = downloadableFiles?.find((f) => f?.type === "legal-note");

  const handleDownload = (type?: string) => {
    if (!type || !downloadDocument) return;
    downloadDocument(type);
  };

  return (
    <section className="upsell-v3__coverage-scope">
      <div className="upsell-v3__coverage-scope-inner">
        <div className="upsell-v3__coverage-scope-grid">
          <aside className="upsell-v3__coverage-scope-left">
            <div className="upsell-v3__eyebrow upsell-v3__coverage-scope-eyebrow">
              {translate("newUpsell.v3.coverageScope.eyebrow")}
            </div>
            <h2 className="upsell-v3__coverage-scope-title">
              {translate("newUpsell.v3.coverageScope.titleLine1")}
              <br />
              {translate("newUpsell.v3.coverageScope.titleLine2")}
            </h2>
            <p className="upsell-v3__coverage-scope-subtitle">
              {translate("newUpsell.v3.coverageScope.subtitle")}
            </p>
            <div
              className="upsell-v3__coverage-scope-tabs"
              role="tablist"
            >
              <button
                type="button"
                role="tab"
                aria-selected={tab === "si"}
                className={`upsell-v3__coverage-scope-tab upsell-v3__coverage-scope-tab--si${
                  tab === "si" ? " is-active" : ""
                }`}
                onClick={() => setTab("si")}
              >
                {translate("newUpsell.v3.coverageScope.tabYes")} ·{" "}
                {yesItems.length}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === "no"}
                className={`upsell-v3__coverage-scope-tab upsell-v3__coverage-scope-tab--no${
                  tab === "no" ? " is-active" : ""
                }`}
                onClick={() => setTab("no")}
              >
                {translate("newUpsell.v3.coverageScope.tabNo")} ·{" "}
                {noItems.length}
              </button>
            </div>
          </aside>

          <div className="upsell-v3__coverage-scope-right">
            <ul
              className="upsell-v3__coverage-scope-items"
              role="tabpanel"
            >
              {items.map((item) => (
                <li
                  key={item.title}
                  className="upsell-v3__coverage-scope-item"
                >
                  <span
                    className={`upsell-v3__coverage-scope-icon upsell-v3__coverage-scope-icon--${tab}`}
                  >
                    {tab === "si" ? <IconCheck size={16} /> : <IconX size={14} />}
                  </span>
                  <div>
                    <div className="upsell-v3__coverage-scope-itemTitle">
                      {item.title}
                    </div>
                    {item.desc && (
                      <div className="upsell-v3__coverage-scope-itemDesc">
                        {item.desc}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            {(generalConditions || legalNote) && (
              <div className="upsell-v3__coverage-scope-docs">
                {generalConditions && (
                  <button
                    type="button"
                    className="upsell-v3__coverage-scope-docLink"
                    onClick={() => handleDownload(generalConditions.type)}
                  >
                    <IconDoc size={13} />
                    {translate("newUpsell.v3.priceCard.docConditions")}
                  </button>
                )}
                {legalNote && (
                  <button
                    type="button"
                    className="upsell-v3__coverage-scope-docLink"
                    onClick={() => handleDownload(legalNote.type)}
                  >
                    <IconDoc size={13} />
                    {translate("newUpsell.v3.priceCard.docPrev")}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default V3CoverageScope;
